import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
    FaCheck,
    FaUser,
    FaMoneyBillWave,
    FaIdCard,
    FaRegClock,
    FaChartBar,
    FaWallet,
    FaExclamationTriangle,
    FaPlus,
    FaEye,
    FaTrash,
    FaHistory,
    FaPauseCircle,
    FaHandHoldingUsd,
    FaWrench // Added for maintenance actions
} from "react-icons/fa";
import {
    deleteLoanDocument,
    getLoansDetails,
    getLoansDocuments,
    pauseEmi,
    uploadLoanDocument,
    recalculateSingleLoanPenalties // Imported new api hook
} from "../../Api/Api";
import UploadDocumentModal from "./UploadDocumentModal";
import { useNotification } from "../Notification/NotificationContext";
import EmiPaymentModal from "./EmiPaymentModal";
import EmiHistoryModal from "./EmiHistoryModal";
import { useWorkshop } from "../Common/WorkshopContext";
import SettleLoanModal from "./SettleLoanModal";

const LoanDetails = () => {
    const { loanId } = useParams();
    const { workshopId } = useWorkshop();
    const [loan, setLoan] = useState(null);
    const [documents, setDocuments] = useState([]);
    const [selectedEmi, setSelectedEmi] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [showEmiHistoryModal, setShowEmiHistoryModal] = useState(false);
    const [activeTab, setActiveTab] = useState("EMIs");
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [docToDelete, setDocToDelete] = useState(null);
    const { showNotification } = useNotification();
    const [showSettlementModal, setShowSettlementModal] = useState(false);

    // New Maintenance states
    const [isFixing, setIsFixing] = useState(false);
    const [migrationLogs, setMigrationLogs] = useState([]);
    const [showFixConfirmModal, setShowFixConfirmModal] = useState(false);

    const isLoanSettled = loan?.status === "SETTLED";

    useEffect(() => {
        fetchLoanDetails();
        fetchDocuments();
    }, [loanId]);

    const fetchLoanDetails = () => {
        getLoansDetails(loanId)
            .then((response) => setLoan(response.data))
            .catch((error) => showNotification({ message: error, type: "error" }));
    };

    const fetchDocuments = () => {
        getLoansDocuments(loanId)
            .then((response) => setDocuments(response.data))
            .catch((error) => showNotification({ message: error, type: "error" }));
    };

    const uploadDocument = (formData) => {
        uploadLoanDocument(loanId, formData)
            .then(() => {
                showNotification("Uploaded Successfully!", "success");
                fetchDocuments();
            })
            .catch((error) => showNotification(error.message, "error"));
    };

    const handlePauseEmi = async (emi) => {
        if (isLoanSettled) return;
        if (!window.confirm("Pause this EMI for one month? A ₹2000 minimum charge will apply.")) return;

        try {
            await pauseEmi(emi.id);
            fetchLoanDetails();
            showNotification({ message: "EMI paused successfully!", type: "success" });
        } catch (error) {
            showNotification({ message: error.message || "Failed to pause EMI", type: "error" });
        }
    };

    const handleDeleteClick = (docId) => {
        setDocToDelete(docId);
        setShowDeleteConfirm(true);
    };

    const confirmDeleteDocument = async () => {
        if (!docToDelete) return;
        try {
            await deleteLoanDocument(docToDelete);
            fetchDocuments();
            setShowDeleteConfirm(false);
            setDocToDelete(null);
            showNotification({ message: "Document Deleted Successfully!", type: "success" });
        } catch (error) {
            showNotification(error.message, "error");
        }
    };

    const payEMI = (emi) => {
        if (isLoanSettled) return;
        setSelectedEmi(emi);
        setShowModal(true);
    };

    const showHistoryOfEmi = (emi) => {
        setSelectedEmi(emi);
        setShowEmiHistoryModal(true);
    };

    const handleSettlementModalShow = () => {
        if (!isLoanSettled) setShowSettlementModal(true);
    };

    // Maintenance Handlers
    const handleRecalculateDryRun = async () => {
        setIsFixing(true);
        try {
            const response = await recalculateSingleLoanPenalties(loanId, true); // dryRun = true
            setMigrationLogs(response.data.logs || []);
            setShowFixConfirmModal(true);
            if (response.data.totalFixed === 0) {
                showNotification({ message: "No corrupted EMIs detected for this loan.", type: "info" });
            }
        } catch (error) {
            showNotification({ message: error.response?.data?.message || "Recalculation analysis failed", type: "error" });
        } finally {
            setIsFixing(false);
        }
    };

    const handleCommitRecalculation = async () => {
        setIsFixing(true);
        setShowFixConfirmModal(false);
        try {
            await recalculateSingleLoanPenalties(loanId, false); // dryRun = false (live save!)
            showNotification({ message: "Loan parameters corrected successfully!", type: "success" });
            fetchLoanDetails();
        } catch (error) {
            showNotification({ message: error.response?.data?.message || "Live data repair failed", type: "error" });
        } finally {
            setIsFixing(false);
        }
    };

    return (
        <div className="max-w-8xl mx-auto mt-6 p-6 bg-white shadow-lg rounded-lg">
            <div className="bg-teal-500 text-white p-4 rounded-t-lg flex items-center justify-between">
                <h2 className="text-xl font-semibold">Loan Details</h2>

                <div className="flex items-center gap-3">
                    {/* Maintenance Button - Available unless settled */}
                    {!isLoanSettled && (
                        <button
                            onClick={handleRecalculateDryRun}
                            disabled={isFixing}
                            className="bg-amber-500 hover:bg-amber-600 text-white px-3 py-2 rounded-md flex items-center gap-2 font-medium transition disabled:opacity-50"
                        >
                            <FaWrench /> {isFixing ? "Analyzing..." : "Fix Loan Penalties"}
                        </button>
                    )}

                    {!isLoanSettled && (
                        <button
                            className="bg-white text-teal-600 px-3 py-2 rounded-md flex items-center gap-2 font-medium hover:bg-gray-100 transition"
                            onClick={() => setShowUploadModal(true)}
                        >
                            <FaPlus /> Upload Document
                        </button>
                    )}

                    {!isLoanSettled && (
                        <button
                            onClick={handleSettlementModalShow}
                            className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-md flex items-center gap-2 font-medium transition"
                        >
                            <FaHandHoldingUsd /> Settle
                        </button>
                    )}
                </div>
            </div>

            {loan && (
                <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {[
                        { icon: <FaIdCard className="text-teal-500 text-2xl" />, label: "Loan ID", value: loan.loanId },
                        {
                            icon: <FaUser className="text-teal-500 text-2xl" />,
                            label: "Customer",
                            value: (
                                <Link
                                    to={`/customers/${loan.customer.id}/workshops/${workshopId}/profile`}
                                    className="text-blue-600 hover:underline"
                                >
                                    {loan.customer.name}
                                </Link>
                            ),
                        },
                        { icon: <FaMoneyBillWave className="text-green-500 text-2xl" />, label: "Loan Amount", value: `₹ ${loan.amount?.toFixed(2)}` },
                        { icon: <FaRegClock className="text-yellow-500 text-2xl" />, label: "Status", value: loan.status },
                        { icon: <FaMoneyBillWave className="text-green-500 text-2xl" />, label: "Down Payment", value: `₹ ${loan.downPayment?.toFixed(2)}` },
                        { icon: <FaWallet className="text-blue-500 text-2xl" />, label: "Principal Paid", value: `₹ ${loan.totalPrincipalPaid?.toFixed(2)}` },
                        { icon: <FaChartBar className="text-purple-500 text-2xl" />, label: "Interest Paid", value: `₹ ${loan.totalInterestPaid?.toFixed(2)}` },
                        {
                            icon: <FaMoneyBillWave className="text-red-500 text-2xl" />,
                            label: "Remaining Principal",
                            value: `₹ ${isLoanSettled ? "0.00" : loan.remainingPrincipal?.toFixed(2)}`
                        },
                        isLoanSettled && {
                            icon: <FaHandHoldingUsd className="text-red-500 text-2xl" />,
                            label: "Settlement Amount",
                            value: `₹ ${loan.loanSettlementAmount?.toFixed(2) || "0.00"}`
                        },
                        { icon: <FaExclamationTriangle className="text-orange-500 text-2xl" />, label: "Overdue EMIs", value: loan.overdueEmiCount },
                        { icon: <FaPauseCircle className="text-yellow-500 text-2xl" />, label: "Pause Count Used", value: loan.pauseCount ?? 0 },
                    ]
                        .filter(Boolean)
                        .map((item, index) => (
                            <div key={index} className="bg-white shadow-md p-4 rounded-lg flex items-center">
                                {item.icon}
                                <div className="ml-3">
                                    <p className="text-gray-600 text-sm">{item.label}</p>
                                    <p className={`text-lg font-semibold`}>{item.value}</p>
                                </div>
                            </div>
                        ))}
                </div>
            )}

            <div className="mt-6">
                <div className="flex border-b">
                    {["EMIs", "Documents"].map((tab) => (
                        <button
                            key={tab}
                            className={`px-6 py-3 text-lg font-semibold transition-all duration-300 ${
                                activeTab === tab ? "text-teal-500 border-b-4 border-teal-500" : "text-gray-500 hover:text-teal-500"
                            }`}
                            onClick={() => setActiveTab(tab)}
                        >
                            {tab}
                        </button>
                    ))}
                </div>

                {activeTab === "EMIs" ? (
                    <div className="overflow-x-auto mt-4 p-4 bg-gray-100 rounded-lg shadow-md">
                        <table className="w-full">
                            <thead className="bg-gray-200 text-gray-800">
                            <tr>
                                <th className="p-3 text-left">ID</th>
                                <th className="p-3 text-left">Date</th>
                                <th className="p-3 text-left">Amount(₹)</th>
                                <th className="p-3 text-left">Penalty(₹)</th>
                                <th className="p-3 text-left">Total Due(₹)</th>
                                <th className="p-3 text-left">Status</th>
                                <th className="p-3 text-left">DPD</th>
                                <th className="p-3 text-left">Due(₹)</th>
                                <th className="p-3 text-left">Total Paid(₹)</th>
                                <th className="p-3 text-left">Actions</th>
                            </tr>
                            </thead>
                            <tbody>
                            {(() => {
                                const nextUnpaidEmi = loan?.emis.find(
                                    (emi) => emi.status !== "PAID" && emi.status !== "PAUSED" && emi.status !== "SETTLED"
                                );

                                return loan?.emis.map((emi) => (
                                    <tr key={emi.id} className="border-b hover:bg-gray-200">
                                        <td className="p-3">{emi.id}</td>
                                        <td className="p-3">{new Date(emi.dueDate).toLocaleDateString()}</td>
                                        <td className="p-3">{emi.amount?.toFixed(2)}</td>
                                        <td className="p-3">{emi?.penaltyAmount?.toFixed(2) || "0.00"}</td>
                                        <td className="p-3">{Number((emi.penaltyAmount || 0) + (emi.amount || 0)).toFixed(2)}</td>
                                        <td className="p-3">
                                            <span
                                                className={`px-3 py-1 rounded-full text-xs ${
                                                    emi.status === "PAID"
                                                        ? "bg-green-200 text-green-800"
                                                        : emi.status === "OVERDUE"
                                                            ? "bg-red-200 text-red-800"
                                                            : emi.status === "PAUSED"
                                                                ? "bg-yellow-200 text-yellow-800"
                                                                : emi.status === "SETTLED"
                                                                    ? "bg-purple-200 text-purple-800"
                                                                    : "bg-blue-200 text-blue-800"
                                                }`}
                                            >
                                                {emi.status}
                                            </span>
                                        </td>
                                        <td className="p-3">{emi.daysLate}</td>
                                        <td className="p-3">
                                            ₹{emi.status === "SETTLED" ? "0.00" : emi.remainingAmount?.toFixed(2)}
                                        </td>
                                        <td className="p-3">{emi?.paidAmount?.toFixed(2) || "0.00"}</td>

                                        <td className="p-3 flex space-x-2">
                                            {emi.status !== "PAID" && emi.status !== "PAUSED" && emi.status !== "SETTLED" && !isLoanSettled && (
                                                <>
                                                    <button
                                                        className="bg-teal-500 text-white px-3 py-1 rounded-md hover:bg-teal-600 flex items-center"
                                                        onClick={() => payEMI(emi)}
                                                    >
                                                        <FaCheck className="mr-2" /> Pay
                                                    </button>

                                                    {emi.id === nextUnpaidEmi?.id && loan.pauseCount < 3 && (
                                                        <button
                                                            className="bg-orange-500 text-white px-3 py-1 rounded-md hover:bg-orange-600 flex items-center"
                                                            onClick={() => handlePauseEmi(emi)}
                                                        >
                                                            <FaRegClock className="mr-2" /> Pause
                                                        </button>
                                                    )}
                                                </>
                                            )}

                                            <button
                                                className="bg-gray-500 text-white px-3 py-1 rounded-md hover:bg-gray-600 flex items-center"
                                                onClick={() => showHistoryOfEmi(emi)}
                                            >
                                                <FaHistory className="mr-2" /> Details
                                            </button>
                                        </td>
                                    </tr>
                                ));
                            })()}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    activeTab === "Documents" && (
                        <div className="overflow-x-auto mt-4 p-4 bg-gray-100 rounded-lg shadow-md">
                            <table className="w-full">
                                <thead className="bg-gray-200 text-gray-800">
                                <tr>
                                    <th className="p-3 text-left">Document Type</th>
                                    <th className="p-3 text-left">Uploaded At</th>
                                    <th className="p-3 text-left">Actions</th>
                                </tr>
                                </thead>
                                <tbody>
                                {documents.length > 0 ? (
                                    documents.map((doc) => (
                                        <tr key={doc.id} className="border-b hover:bg-gray-200">
                                            <td className="p-3">{doc.documentType}</td>
                                            <td className="p-3">{new Date(doc.uploadedAt).toLocaleString()}</td>
                                            <td className="p-3 flex space-x-2">
                                                <a
                                                    href={doc.fileUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="bg-blue-500 text-white px-3 py-1 rounded-md hover:bg-blue-600 flex items-center"
                                                >
                                                    <FaEye className="mr-1" /> View
                                                </a>
                                                {!isLoanSettled && (
                                                    <button
                                                        onClick={() => handleDeleteClick(doc.id)}
                                                        className="bg-red-500 text-white px-3 py-1 rounded-md hover:bg-red-600 flex items-center"
                                                    >
                                                        <FaTrash className="mr-1" /> Delete
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td className="p-3 text-gray-500" colSpan="3">
                                            No Documents Available
                                        </td>
                                    </tr>
                                )}
                                </tbody>
                            </table>
                        </div>
                    )
                )}
            </div>

            {/* Dry Run Audit Confirmation Modal */}
            {showFixConfirmModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full flex flex-col max-h-[85vh]">
                        <div className="bg-amber-500 text-white p-4 rounded-t-lg">
                            <h3 className="text-lg font-semibold flex items-center gap-2">
                                <FaWrench /> Recalculation Audit Log (Dry Run Results)
                            </h3>
                        </div>
                        <div className="p-6 overflow-y-auto flex-1 bg-gray-50 font-mono text-sm">
                            <p className="text-gray-700 font-sans font-medium mb-3">
                                Review the proposed ledger corrections below before writing them to the database:
                            </p>
                            {migrationLogs.length > 0 ? (
                                <div className="space-y-2">
                                    {migrationLogs.map((logStr, idx) => (
                                        <div key={idx} className="p-3 bg-white border border-gray-200 rounded text-gray-800 shadow-sm leading-relaxed whitespace-pre-wrap">
                                            {logStr}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-gray-500 italic p-4 bg-white border rounded text-center font-sans">
                                    No anomalies detected. Every EMI on this loan is structurally sound.
                                </p>
                            )}
                        </div>
                        <div className="p-4 border-t flex justify-end gap-3 bg-gray-100 rounded-b-lg">
                            <button
                                className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-md font-medium"
                                onClick={() => setShowFixConfirmModal(false)}
                            >
                                Close View
                            </button>
                            {migrationLogs.length > 0 && (
                                <button
                                    className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-md font-medium"
                                    onClick={handleCommitRecalculation}
                                >
                                    Apply Adjustments to Database
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Modals */}
            {showUploadModal && (
                <UploadDocumentModal
                    loanId={loanId}
                    onClose={() => setShowUploadModal(false)}
                    handleSave={uploadDocument}
                />
            )}

            {showEmiHistoryModal && selectedEmi && (
                <EmiHistoryModal
                    emiId={selectedEmi.id}
                    onClose={() => setShowEmiHistoryModal(false)}
                />
            )}

            {showModal && selectedEmi && (
                <EmiPaymentModal
                    emi={selectedEmi}
                    onClose={() => setShowModal(false)}
                    onPaymentSuccess={() => {
                        setShowModal(false);
                        fetchLoanDetails();
                    }}
                />
            )}

            {showSettlementModal && (
                <SettleLoanModal
                    show={showSettlementModal}
                    loanId={loanId}
                    onClose={() => setShowSettlementModal(false)}
                    onSettled={fetchLoanDetails}
                    onSuccess={fetchLoanDetails}
                />
            )}

            {showDeleteConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-6 rounded-lg shadow-lg w-96">
                        <h3 className="text-lg font-semibold text-gray-800">Confirm Delete</h3>
                        <p className="text-gray-600 mt-2">
                            Are you sure you want to delete this document? This action cannot be undone.
                        </p>
                        <div className="flex justify-end mt-4">
                            <button
                                className="bg-gray-300 text-gray-800 px-4 py-2 rounded-lg mr-2 hover:bg-gray-400"
                                onClick={() => setShowDeleteConfirm(false)}
                            >
                                Cancel
                            </button>
                            <button
                                className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600"
                                onClick={confirmDeleteDocument}
                            >
                                Yes, Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LoanDetails;